import {
  formatDisplayAmount,
  formatDisplayUnits,
} from "../helpers/amountDisplay";
import { WalletAmountInput } from "./WalletAmountInput";
import React from "react";
import { styles } from "../../styles";
import { EVM_CONFIG, type TokenInfo } from "../../config";
import type { OdosQuoteResponse } from "../hooks/useBaseSwapQuote";
import { ethers } from "ethers";
import {
  privateSwapDeployment,
  isQualifiedPrivateSwapToken,
} from "../../joinsplit-v2/privateSwapConfig";
import { BaseOperationProgress } from "./BaseOperationProgress";

interface BaseSwapTabProps {
  enabled: boolean;
  chainLabel: string;
  tokens: TokenInfo[];
  inputToken: TokenInfo;
  setInputToken: (t: TokenInfo) => void;
  outputToken: TokenInfo;
  setOutputToken: (t: TokenInfo) => void;
  amount: string;
  setAmount: (v: string) => void;
  slippageBps: string;
  setSlippageBps: (v: string) => void;
  inputPrivateBalance: string;
  quote: OdosQuoteResponse | null;
  quoteLoading: boolean;
  quoteError: string | null;
  loading: boolean;
  connected: boolean;
  spendingKeysReady: boolean;
  onSwap: () => void;
  onRefreshQuote?: () => void;
  progressMessage?: string;
  completed?: boolean;
}

function formatOutputAmount(
  raw: string,
  decimals: number,
  round: "down" | "up" = "down",
): string {
  return formatDisplayUnits(raw || "0", decimals, round);
}

export function BaseSwapTab({
  enabled,
  chainLabel,
  tokens,
  inputToken,
  setInputToken,
  outputToken,
  setOutputToken,
  amount,
  setAmount,
  slippageBps,
  setSlippageBps,
  inputPrivateBalance,
  quote,
  quoteLoading,
  quoteError,
  loading,
  connected,
  spendingKeysReady,
  onSwap,
  onRefreshQuote,
  progressMessage,
  completed = false,
}: BaseSwapTabProps) {
  const swapTokens = privateSwapDeployment()
    ? tokens.filter((t) => isQualifiedPrivateSwapToken(t.address))
    : tokens;
  let amountError: string | null = null;
  try {
    const value = ethers.parseUnits(amount || "0", inputToken.decimals),
      balance = ethers.parseUnits(
        inputPrivateBalance || "0",
        inputToken.decimals,
      );
    if (amount.trim() && value <= 0n)
      amountError = "Enter a valid amount greater than zero.";
    else if (value > balance)
      amountError = "Insufficient available private balance.";
  } catch {
    amountError = "Enter a valid amount.";
  }
  const canSwap =
    connected &&
    spendingKeysReady &&
    !loading &&
    !quoteLoading &&
    enabled &&
    !!quote?.pathId &&
    swapTokens.some((t) => t.address === inputToken.address) &&
    swapTokens.some((t) => t.address === outputToken.address) &&
    !amountError &&
    parseFloat(amount) > 0;
  const swapProtocolFeeBps = Math.max(
    0,
    quote?.privateSwap?.protocolFeeBps ??
      Number(EVM_CONFIG.SWAP_PROTOCOL_FEE_BPS || 0),
  );
  const swapPartnerFeeBps = Math.max(
    0,
    quote?.privateSwap ? 0 : Number(EVM_CONFIG.SWAP_PARTNER_FEE_BPS || 0),
  );
  const swapTotalFeeBps = swapProtocolFeeBps + swapPartnerFeeBps;

  if (!enabled) {
    return (
      <div className="wallet-swap-unavailable" role="status">
        <p>Swap assets in your private balance.</p>
        <p className="wallet-address">
          Swaps aren’t available on {chainLabel} yet.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ marginBottom: "16px", color: "#e8eeff" }}>Swap</h3>
      <p style={{ fontSize: "14px", color: "#b0c4de", marginBottom: "16px" }}>
        Swap assets from your private balance. The received asset returns to
        your private balance.
      </p>

      {/* Input token */}
      <div style={{ marginBottom: "12px" }}>
        <label
          htmlFor="swap-input-token"
          style={{ ...styles.label, color: "#b0c4de", fontSize: "13px" }}
        >
          From
        </label>
        <select
          id="swap-input-token"
          disabled={loading}
          value={inputToken.address}
          onChange={(e) => {
            const t = tokens.find((tk) => tk.address === e.target.value);
            if (t) setInputToken(t);
          }}
          style={styles.input}
        >
          {swapTokens.map((t) => (
            <option key={t.address} value={t.address}>
              {t.symbol}
            </option>
          ))}
        </select>
      </div>

      {/* Amount */}
      <div style={{ marginBottom: "12px" }}>
        <label
          htmlFor="swap-amount"
          style={{ ...styles.label, color: "#b0c4de", fontSize: "13px" }}
        >
          Amount
        </label>
        <WalletAmountInput
          id="swap-amount"
          disabled={loading}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.0"
          onMax={() => setAmount(inputPrivateBalance)}
          maxDisabled={loading || !(Number(inputPrivateBalance) > 0)}
        />
        {inputPrivateBalance && (
          <div style={{ fontSize: "12px", color: "#b0c4de", marginTop: "4px" }}>
            Private balance: {formatDisplayAmount(inputPrivateBalance)}{" "}
            {inputToken.symbol}
          </div>
        )}
        {amountError && (
          <div
            role="alert"
            style={{ color: "#ff5c70", fontSize: "13px", marginTop: "6px" }}
          >
            {amountError}
          </div>
        )}
      </div>

      {/* Output token */}
      <div style={{ marginBottom: "12px" }}>
        <label
          htmlFor="swap-output-token"
          style={{ ...styles.label, color: "#b0c4de", fontSize: "13px" }}
        >
          To
        </label>
        <select
          id="swap-output-token"
          disabled={loading}
          value={outputToken.address}
          onChange={(e) => {
            const t = tokens.find((tk) => tk.address === e.target.value);
            if (t) setOutputToken(t);
          }}
          style={styles.input}
        >
          {swapTokens.map((t) => (
            <option key={t.address} value={t.address}>
              {t.symbol}
            </option>
          ))}
        </select>
      </div>

      {/* Slippage */}
      <div style={{ marginBottom: "12px" }}>
        <label
          htmlFor="swap-slippage"
          style={{ ...styles.label, color: "#b0c4de", fontSize: "13px" }}
        >
          Slippage tolerance (bps)
        </label>
        <input
          id="swap-slippage"
          disabled={loading}
          type="number"
          min="1"
          max="100"
          value={slippageBps}
          onChange={(e) => {
            const next = Math.max(
              1,
              Math.min(100, Math.trunc(Number(e.target.value || "1"))),
            );
            setSlippageBps(String(Number.isFinite(next) ? next : 50));
          }}
          style={styles.input}
        />
        <div style={{ fontSize: "11px", color: "#667a99", marginTop: "2px" }}>
          {Number(slippageBps) / 100}% max
        </div>
      </div>

      {/* Quote display */}
      <div
        style={{
          fontSize: "12px",
          color: "#b0c4de",
          marginBottom: "12px",
          padding: "10px",
          background: "rgba(255,255,255,0.03)",
          borderRadius: "8px",
        }}
      >
        {quoteLoading ? (
          <div>Fetching quote...</div>
        ) : quote?.privateFeesSwap ? (
          <div>
            <div>
              Protocol fee (
              {quote.privateFeesSwap.policy.rates.swap.protocolBps / 100}%):{" "}
              {formatDisplayUnits(
                quote.privateFeesSwap.protocolFee,
                inputToken.decimals,
                "up",
              )}{" "}
              {inputToken.symbol}
            </div>
            <div>
              Partner fee (
              {quote.privateFeesSwap.policy.rates.swap.partnerBps / 100}%):{" "}
              {formatDisplayUnits(
                quote.privateFeesSwap.partnerFee,
                inputToken.decimals,
                "up",
              )}{" "}
              {inputToken.symbol}
            </div>
            <div>
              Estimated network fee:{" "}
              {formatDisplayUnits(
                quote.privateFeesSwap.gasFee,
                inputToken.decimals,
                "up",
              )}{" "}
              {inputToken.symbol}
            </div>
            <div style={{ marginTop: "8px" }}>
              Estimated received:{" "}
              <strong>
                {formatDisplayUnits(
                  quote.privateFeesSwap.amountOut,
                  outputToken.decimals,
                )}{" "}
                {outputToken.symbol}
              </strong>
            </div>
            <div>
              Minimum received:{" "}
              {formatDisplayUnits(
                (BigInt(quote.privateFeesSwap.amountOut) *
                  BigInt(10000 - Number(slippageBps))) /
                  10000n,
                outputToken.decimals,
              )}{" "}
              {outputToken.symbol}
            </div>
            <div style={{ marginTop: "4px" }}>
              Fees are included in your amount. The received asset returns to
              your private balance.
            </div>
          </div>
        ) : quote?.outAmounts?.[0] ? (
          (() => {
            const grossOutput = BigInt(quote.outAmounts[0] || "0");
            const protocolFee =
              swapProtocolFeeBps > 0
                ? (grossOutput * BigInt(swapProtocolFeeBps)) / 10_000n
                : 0n;
            const partnerFee =
              swapPartnerFeeBps > 0
                ? (grossOutput * BigInt(swapPartnerFeeBps)) / 10_000n
                : 0n;
            const totalFee = protocolFee + partnerFee;
            const netOutput =
              grossOutput > totalFee ? grossOutput - totalFee : 0n;
            return (
              <div>
                <div style={{ marginBottom: "4px" }}>
                  Estimated output:{" "}
                  <strong style={{ color: "#e8eeff" }}>
                    {formatOutputAmount(
                      quote.outAmounts[0],
                      outputToken.decimals,
                    )}{" "}
                    {outputToken.symbol}
                  </strong>
                </div>
                {swapTotalFeeBps > 0 ? (
                  <>
                    <div>
                      Swap fee ({swapTotalFeeBps} bps):{" "}
                      {formatOutputAmount(
                        totalFee.toString(),
                        outputToken.decimals,
                        "up",
                      )}{" "}
                      {outputToken.symbol}
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      Estimated net output:{" "}
                      <strong style={{ color: "#e8eeff" }}>
                        {formatOutputAmount(
                          netOutput.toString(),
                          outputToken.decimals,
                        )}{" "}
                        {outputToken.symbol}
                      </strong>
                    </div>
                  </>
                ) : (
                  <div style={{ marginBottom: "4px" }}>Protocol fee: 0%</div>
                )}
                {quote.privateSwap && (
                  <>
                    <div>
                      Relayer fee:{" "}
                      {formatOutputAmount(
                        quote.privateSwap.relayFee,
                        inputToken.decimals,
                        "up",
                      )}{" "}
                      {inputToken.symbol} (included in your amount)
                    </div>
                    <div>
                      {quote.privateSwap.venue === "muchfi-v2"
                        ? "MuchFi pool fees included in quote"
                        : `Uniswap pool fee: ${quote.privateSwap.feeTier / 10_000}% (included in quote)`}
                    </div>
                    <div>
                      Minimum received:{" "}
                      {formatOutputAmount(
                        (() => {
                          const minimum =
                            (grossOutput *
                              BigInt(10_000 - Number(slippageBps))) /
                            10_000n;
                          return (
                            minimum -
                            (minimum * BigInt(swapProtocolFeeBps)) / 10_000n
                          ).toString();
                        })(),
                        outputToken.decimals,
                      )}{" "}
                      {outputToken.symbol}
                    </div>
                  </>
                )}
                {!quote.privateSwap && quote.priceImpact !== undefined && (
                  <div
                    style={{
                      color:
                        Math.abs(quote.priceImpact) > 3 ? "#ff6b6b" : "#b0c4de",
                    }}
                  >
                    Price impact: {quote.priceImpact.toFixed(2)}%
                  </div>
                )}
                {quote.gasEstimate > 0 && (
                  <div>Gas estimate: ~{quote.gasEstimate.toLocaleString()}</div>
                )}
                <div style={{ marginTop: "4px", color: "#8fa4c4" }}>
                  The received output returns to your private balance.
                </div>
              </div>
            );
          })()
        ) : quoteError ? (
          <div style={{ color: "#ff8888" }}>{quoteError}</div>
        ) : (
          <div>Enter an amount to get a quote</div>
        )}
      </div>

      {/* Swap button */}
      {loading && (
        <BaseOperationProgress
          message={progressMessage || "Preparing transaction…"}
          extendedPreparationMessage="Processing your transaction…"
          completed={completed}
        />
      )}
      {onRefreshQuote && (
        <button
          type="button"
          onClick={onRefreshQuote}
          disabled={loading || quoteLoading || !amount}
          style={{ ...styles.button, marginBottom: "12px", width: "100%" }}
        >
          Refresh quote
        </button>
      )}
      <button
        className="wallet-cta"
        style={{
          ...styles.button,
          background: "#f0f4ff",
          color: "#06060f",
          width: "100%",
          opacity: canSwap ? 1 : 0.6,
        }}
        onClick={onSwap}
        disabled={!canSwap}
      >
        {loading ? "Swapping..." : "Swap Privately"}
      </button>
    </div>
  );
}
